import {
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogClose,
    MatDialogRef,
} from '@angular/material/dialog';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';

import { AutocompleteFixersComponent } from '@shared/autocomplete/autocomplete-fixers/autocomplete-fixers.component';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { ChipsAutocompleteComponent } from '@shared/chips-autocomplete/chips-autocomplete.component';
import { SelectOption, SelectOptionWithGroup } from '@models/shared.model';
import { UserInfo, UserKind, UserKindLabel } from '@models/user';
import { DiscrepancyFixers } from '@models/discrepancy';
import {
    AutomationFieldKind,
    AutomationRuleTypeGroup,
} from '@models/automation';
import {
    automationFieldIdsSharingTarget,
    automationInputFieldIdsOfKind,
    automationOutputFieldIdsOfKind,
    flattenAutomationRules,
} from '@helpers/automation-field.helpers';
import {
    buildAutomationCascadeGraph,
    evaluateAutomationCascade,
} from '@helpers/automation-cascade.helpers';
import { AutomationService } from '@services/api/automation/automation.service';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import DATA_QA from '@automation/data-qa.json';

@Component({
    templateUrl: 'add-fixers-dialog.component.html',
    imports: [
        MatDialogClose,
        ReactiveFormsModule,
        FlatButtonComponent,
        AutocompleteFixersComponent,
        ChipsAutocompleteComponent,
    ],
})
export class AddFixersDialogComponent implements OnInit, OnDestroy {
    form!: FormGroup;
    primaryFixerOptions: SelectOptionWithGroup[] = [];
    fixerOptions: SelectOptionWithGroup[] = [];
    disabledOptions: (string | number)[] = [];
    invalid: boolean = false;
    /**
     * Drives the `[externalSelection]` binding on the chips component.
     * Stays `null` until automation rules produce a result so that the chips
     * component does not clear an existing manual selection on first render.
     */
    automationSecondaryFixers: SelectOption[] | null = null;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    readonly dataQa = DATA_QA;

    private readonly data: {
        currentFixers: DiscrepancyFixers;
        fixerOptions: UserInfo[];
        primaryFixerOptions?: UserInfo[];
        automationRuleGroups?: AutomationRuleTypeGroup[];
        stationId?: number;
    } = inject(MAT_DIALOG_DATA);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialogRef = inject(MatDialogRef<AddFixersDialogComponent>);
    private readonly automationService = inject(AutomationService);
    private readonly cdr = inject(ChangeDetectorRef);

    constructor() {
        this.fixerOptions = this.groupByKind(this.data.fixerOptions);
        this.primaryFixerOptions = this.groupByKind(
            this.data.primaryFixerOptions ?? this.data.fixerOptions,
        );
    }

    get primaryFixer() {
        return this.form.get('primaryFixer') as FormControl;
    }

    get secondaryFixers() {
        return this.form.get('secondaryFixers') as FormControl;
    }

    get saveDisabled() {
        return this.form.invalid || !this.primaryFixer.value.value;
    }

    ngOnInit(): void {
        this.initForm();
        this.ensureCurrentPrimaryFixerAllowed();
        this.listenPrimaryFixerChange();
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribeAllSafe();
    }

    protected save() {
        const fixer = this.data.fixerOptions.find(
            (option) => option.id === this.form.value.primaryFixer.value,
        );
        const secondaryFixersSet = new Set(
            this.form.value.secondaryFixers.map((i: SelectOption) => i.value),
        );
        const secondaryFixers = this.data.fixerOptions.filter((option) =>
            secondaryFixersSet.has(option.id),
        );

        this.dialogRef.close({
            discrepancyFixers: new DiscrepancyFixers(
                fixer ?? null,
                secondaryFixers,
            ),
        });
    }

    private initForm() {
        this.form = this.formBuilder.group({
            primaryFixer: new FormControl<SelectOption | null>(
                this.getCurrentPrimaryFixer(),
                Validators.required,
            ),
            secondaryFixers: new FormControl(this.getCurrentAdditionalFixers()),
        });
    }

    private getCurrentPrimaryFixer(): SelectOption | null {
        let currentPrimaryFixer = null;

        if (this.data.currentFixers) {
            const { primaryFixer } = this.data.currentFixers;

            if (primaryFixer) {
                const { id, fullName, email } = primaryFixer;

                currentPrimaryFixer = {
                    value: id,
                    viewValue: `${fullName} (${email})`,
                };
            }
        }

        return currentPrimaryFixer;
    }

    private getCurrentAdditionalFixers(): SelectOption[] {
        return (this.data.currentFixers.secondaryFixers ?? []).map((i) => ({
            value: i.id,
            viewValue: `${i.fullName} (${i.email})`,
        }));
    }

    private listenPrimaryFixerChange() {
        if (this.getCurrentPrimaryFixer()) {
            this.disabledOptions = [this.getCurrentPrimaryFixer()!.value];
        }

        this._subscriptions['primary-fixer-change'] =
            this.primaryFixer.valueChanges.subscribe((data) => {
                if (data?.value) {
                    this.disabledOptions = [data.value];
                    this.applyAutomationRulesForSecondaryFixers(data.value);
                }
            });
    }

    private applyAutomationRulesForSecondaryFixers(
        primaryFixerId: number,
    ): void {
        const groups = this.data.automationRuleGroups;
        // Per spec: do not touch secondary fixers if no automation rules exist.
        if (!groups || groups.length === 0 || !this.data.stationId) return;

        const rules = flattenAutomationRules(groups);
        if (rules.length === 0) return;

        // One control feeds every field id of its kind: rule types declare
        // their own field for the same station or fixer.
        const stationFieldIds = automationInputFieldIdsOfKind(
            rules,
            AutomationFieldKind.Station,
        );
        const fixerFieldIds = automationInputFieldIdsOfKind(
            rules,
            AutomationFieldKind.Fixer,
        );
        // Every rule type can carry its own secondary fixer output field:
        // `AutomationField.Kind` is not unique across them.
        const secondaryFixerFieldIds = automationOutputFieldIdsOfKind(
            rules,
            AutomationFieldKind.SecondaryFixer,
        );
        const secondaryFixerFieldIdSet = new Set(secondaryFixerFieldIds);
        // Only rule types that can output a secondary fixer take part in the
        // set/clear/preserve decision below. With unconditional rule types
        // included, an unrelated no-input/all-wildcard rule type (e.g. one that
        // defaults a Reason or Directions) would otherwise fill `outputMaps`
        // and be read as "automation manages this fixer", wiping the user's
        // manual secondary fixer selection even though nothing produced one.
        const producesSecondaryFixer = (group: AutomationRuleTypeGroup) =>
            group.outputFieldIds.some((fieldId) =>
                secondaryFixerFieldIdSet.has(fieldId),
            );

        const stationValue = this.data.stationId.toString();
        const currentValues = new Map<number, string | null>();
        stationFieldIds.forEach((fieldId) =>
            currentValues.set(fieldId, stationValue),
        );
        fixerFieldIds.forEach((fieldId) =>
            currentValues.set(fieldId, primaryFixerId.toString()),
        );

        // Chained rule types have to be fed with what the previous ones
        // produced (Fixer -> Reason -> SecondaryFixer), so the same cascade
        // engine as the discrepancy screens drives the evaluation. The dialog
        // renders no control for the intermediate fields, so a rule value is
        // simply carried as it is configured. Every rule type is still matched
        // on its own, so a conflict inside one of them cannot suppress the
        // secondary fixers another one produces.
        const outputMaps: Map<number, string>[] = [];
        evaluateAutomationCascade({
            groups,
            graph: buildAutomationCascadeGraph(groups, (fieldId) =>
                automationFieldIdsSharingTarget(rules, fieldId),
            ),
            changedFieldId: [...stationFieldIds, ...fixerFieldIds],
            currentValues,
            // The dialog derives the secondary fixers from scratch rather than
            // reacting to one field of a filled form, so the rule types
            // matching whatever the values are take part as well.
            includeUnconditionalRuleTypes: true,
            host: {
                matchRules: (group, values) => {
                    const outputs = this.automationService.matchRules(
                        group,
                        values,
                    );
                    // Keep returning the full outputs so the cascade chaining
                    // is unaffected, but only let secondary-fixer-producing
                    // rule types drive the set/clear/preserve decision.
                    if (outputs && producesSecondaryFixer(group)) {
                        outputMaps.push(outputs);
                    }
                    return outputs;
                },
                prepareWrite: (_fieldId, ruleValue) => ({
                    controlValue: ruleValue,
                    canonical: ruleValue === '' ? null : ruleValue,
                }),
                fieldIdsSharingTarget: (fieldId) =>
                    automationFieldIdsSharingTarget(rules, fieldId),
            },
        });

        // If no secondary-fixer-producing rule matched, do not touch secondary
        // fixers (preserve the user's selection).
        if (outputMaps.length === 0) return;

        // Every matching rule type contributes: two of them can produce
        // secondary fixers on their own field, and dropping the later one
        // would silently lose fixers the rules asked for.
        const secondaryFixerOutputs = outputMaps.flatMap((outputs) =>
            secondaryFixerFieldIds
                .map((fieldId) => outputs.get(fieldId))
                .filter((value): value is string => value !== undefined),
        );

        // If a rule matches but has no SecondaryFixer output, clear secondary fixers
        // regardless of whether they were automation-set or manually selected.
        if (secondaryFixerOutputs.length === 0) {
            this.automationSecondaryFixers = [];
            this.secondaryFixers.setValue([], { emitEvent: false });
            // Trigger parent change detection so child chips component (OnPush)
            // sees the externalSelection binding update and clears its UI.
            this.cdr.markForCheck();
            return;
        }

        // Parse comma-separated IDs; empty output → clear selection.
        const secondaryFixerIds = [
            ...new Set(
                secondaryFixerOutputs.flatMap((output) =>
                    output
                        .split(',')
                        .map((id) => id.trim())
                        .filter((id) => id !== '')
                        .map(Number)
                        .filter((id) => !Number.isNaN(id)),
                ),
            ),
        ];

        const secondaryFixersToSet: SelectOption[] = secondaryFixerIds
            .map((id) => this.data.fixerOptions.find((f) => f.id === id))
            .filter((f): f is UserInfo => !!f)
            .map((f) => ({
                value: f.id,
                viewValue: `${f.fullName} (${f.email})`,
            }));

        // Update via the chips component's externalSelection input rather than
        // FormControl.setValue(), because ChipsAutocompleteComponent manages its
        // own internal signal and does not react to programmatic setValue calls.
        this.automationSecondaryFixers = secondaryFixersToSet;
        this.secondaryFixers.setValue(secondaryFixersToSet, {
            emitEvent: false,
        });
        // Trigger parent change detection so child chips component (OnPush)
        // sees the externalSelection binding update.
        this.cdr.markForCheck();
    }

    private ensureCurrentPrimaryFixerAllowed() {
        const current = this.getCurrentPrimaryFixer();
        if (!current) return;

        const allowedIds = new Set(
            (this.data.primaryFixerOptions ?? this.data.fixerOptions).map(
                (i) => i.id,
            ),
        );

        const currentId =
            typeof current.value === 'number'
                ? current.value
                : Number(current.value);

        if (!allowedIds.has(currentId)) {
            this.primaryFixer.setValue(null);
        }
    }

    private groupByKind(data: UserInfo[]): SelectOptionWithGroup[] {
        const grouped: { key: UserKind; value: SelectOption[] }[] = [
            { key: UserKind.DistributionList, value: [] },
            { key: UserKind.Person, value: [] },
        ];

        data.forEach(({ id, kind, fullName, email, status }) => {
            const viewValue = `${fullName} (${email})`;
            const item = grouped.find((group) => group.key === kind);
            const disabled = status === 0;
            item?.value.push({ value: id, viewValue, disabled });
        });

        return grouped.map(({ key, value }) => ({
            group: UserKindLabel[key],
            items: value,
        }));
    }
}
